import React, { useState, useEffect, useRef } from 'react'
import { Tree, Input, Message } from '@arco-design/web-react'
import {
  IconFile,
  IconFolder,
  IconDriveFile,
} from '@arco-design/web-react/icon'
import {
  IconSuccessTip,
  IconWarnTip,
  IconErrorTip,
} from '@arco-iconbox/react-aidb-v2'
import {
  getBookLibraryContentData,
  getBookLibraryContentDetails,
} from './utils/api'
import Empty from './Empty'
import ModalCustom from '../../components/ModalCustom'
import SearchTextMod from './SearchTextMod'
import Amplify from '../Amplify'

import styles from './style/search-content-mod.module.less'

const TreeNode = Tree.Node

interface Props {
  uuid: string
}

const ContextTree = (props: Props) => {
  const { uuid } = props

  // 详情显示弹窗
  const [visible, setVisible] = useState<boolean>(false)
  // 树原始数据
  const [treeDataOriginal, setTreeDataOriginal] = useState<Array<any>>([])
  // 树数据
  const [treeData, setTreeData] = useState<Array<any>>([])
  // 搜索上下文loading
  const [searchContentDataLoading, setSearchContentDataLoading] =
    useState<boolean>(false)
  // 上下文放大弹窗
  const [amplifyContentVisible, setAmplifyContentVisible] =
    useState<boolean>(false)
  // 展开的节点
  const [expandedKeys, setExpandedKeys] = useState<Array<string>>([])
  // 树搜索
  const [inputValue, setInputValue] = useState<string>('')
  // 点击详情值
  const [selectValue, setSelectValue] = useState<string>('')
  // 正文内容不带标签
  const [mainContent, setMainContent] = useState<string>('')

  useEffect(() => {
    setSearchContentDataLoading(true)
    // 书籍库上下文
    getBookLibraryContentData(uuid)
      .then((res) => {
        if (res.status === 200) {
          setTreeDataOriginal(res.data)
          setTreeData(res.data)
        } else {
          Message.error({
            icon: <IconErrorTip useCurrentColor={false} />,
            content: '搜索失败，请重试！',
          })
        }
        setSearchContentDataLoading(false)
      })
      .catch((err) => {
        Message.error({
          icon: <IconErrorTip useCurrentColor={false} />,
          content: '搜索失败，请重试！',
        })
        setSearchContentDataLoading(false)
      })
  }, [])

  useEffect(() => {
    if (!inputValue) {
      setTreeData(JSON.parse(JSON.stringify(treeDataOriginal)))
      setExpandedKeys([])
    } else {
      const { result, keys } = searchData(
        inputValue,
        JSON.parse(JSON.stringify(treeData)),
      )
      setTreeData(result)
      setExpandedKeys(keys)
    }
  }, [inputValue])

  // 搜索树
  function searchData(inputValue, data) {
    const loop = (data, parentKey = []) => {
      let result = []
      let keys = []

      data.forEach((item) => {
        let currentKey = [...parentKey, item.key]
        if (item.title.toLowerCase().indexOf(inputValue.toLowerCase()) > -1) {
          keys.push(...currentKey)
          result.push({ ...item })
        }
        if (item.children) {
          const childrenResult = loop(item.children, currentKey)
          keys = [...keys, ...childrenResult.keys]

          if (childrenResult.result.length) {
            result.push({ ...item, children: childrenResult.result })
          }
        }
      })

      return { result, keys }
    }

    return loop(data)
  }

  // 树节点
  function renderTreeNodes(data) {
    return data.map((item) => (
      <TreeNode
        // icon={item.children ? <IconFolder /> : <IconFile />}
        icons={{
          switcherIcon:
            item.children && item.children.length ? (
              <IconFolder />
            ) : (
              <IconFile />
            ),
        }}
        key={item.key}
        title={item.title}>
        {item.children ? renderTreeNodes(item.children) : null}
      </TreeNode>
    ))
  }

  // 上下文详情搜索
  const openContentDetails = (selectValue: string) => {
    setMainContent('')
    setVisible(true)
    // 书籍库上下文详情点击
    getBookLibraryContentDetails(selectValue)
      .then((res) => {
        console.log(res)
        if (res.status === 200) {
          let result = ''
          res.data.forEach((item) => {
            if (!result) {
              result = result.concat(item.para.properties.paraRaw)
            } else {
              result = result
                .concat('<br />')
                .concat(item.para.properties.paraRaw)
            }
          })
          setMainContent(result)
        } else {
          Message.error({
            icon: <IconErrorTip useCurrentColor={false} />,
            content: '搜索失败，请重试！',
          })
        }
      })
      .catch((err) => {
        Message.error({
          icon: <IconErrorTip useCurrentColor={false} />,
          content: '搜索失败，请重试！',
        })
      })
  }

  // 树节点
  const TreeElement = () => {
    return (
      <>
        <div className={styles.arrow}></div>
        {/* 搜索框 */}
        <Input.Search
          allowClear
          autoFocus
          value={inputValue}
          onChange={setInputValue}
          style={{
            width: '100%',
            marginBottom: 8,
          }}
        />
        {/* 书籍节点 */}
        <Tree
          showLine
          blockNode
          // expandedKeys={expandedKeys}
          autoExpandParent={true}
          size={amplifyContentVisible ? 'large' : 'mini'}
          // onExpand={(keys, extra) => setExpandedKeys(keys)}
          onSelect={(selectedKeys, extra) => {
            if (amplifyContentVisible) setAmplifyContentVisible(false)
            setSelectValue(extra.node.props.title.toString())
            openContentDetails(extra.node.props.title.toString())
          }}
          renderTitle={({ title }) => {
            if (typeof title === 'string' && inputValue) {
              const index = title
                .toLowerCase()
                .indexOf(inputValue.toLowerCase())
              if (index === -1) {
                return title
              }
              const prefix = title.substr(0, index)
              const suffix = title.substr(index + inputValue.length)
              return (
                <span>
                  {prefix}
                  <span style={{ color: '#AC1E1E' }}>
                    {title.substr(index, inputValue.length)}
                  </span>
                  {suffix}
                </span>
              )
            }

            return title
          }}
          style={{
            maxHeight: amplifyContentVisible ? 'calc(100% - 40px)' : '150px',
            overflow: 'auto',
          }}>
          {renderTreeNodes(treeDataOriginal)}
        </Tree>
      </>
    )
  }

  return (
    <div className={styles.context}>
      {treeDataOriginal.length ? (
        <>
          {/* 放大 */}
          <Amplify
            right={-16}
            top={-16}
            title="上下文"
            visible={amplifyContentVisible}
            setVisible={setAmplifyContentVisible}
            content={
              <div className={styles.amplifyContent}>
                <TreeElement />
              </div>
            }
          />
          {/* 树节点 */}
          <TreeElement />
          {/*正文内容*/}
          <ModalCustom
            width={'650px'}
            className={styles.textPop}
            title={null}
            visible={visible}
            close={() => setVisible(false)}
            content={
              <SearchTextMod
                setVisible={setVisible}
                content={mainContent}
                keyWord={selectValue}
                title={selectValue}
              />
            }
          />
        </>
      ) : (
        <Empty state={searchContentDataLoading} />
      )}
    </div>
  )
}

export default ContextTree
